import { createReduxStore, register } from "@wordpress/data";
import type { StoreDescriptor } from "@wordpress/data";
import {
	type Intent,
	type Stage,
	initialStageForIntent,
} from "./auth-state-machine";

export const AUTH_STORE_NAME = "leat/auth" as const;

type CopyOverrides = Partial<{
	modalTitle: string;
	submitText: string;
	successTitle: string;
	successMessage: string;
	accountCreationNote: string;
}>;

type OpenModalOpts = {
	intent: Intent;
	prefillEmail?: string;
	copy?: CopyOverrides;
};

export type AuthState = {
	isOpen: boolean;
	intent: Intent;
	stage: Stage;
	email: string;
	prefillFirstName: string;
	prefillLastName: string;
	error: string | null;
	isSubmitting: boolean;
	canFlipToSignup: boolean;
	copy: CopyOverrides;
};

const DEFAULT_STATE: AuthState = {
	isOpen: false,
	intent: "auto",
	stage: "emailEntry",
	email: "",
	prefillFirstName: "",
	prefillLastName: "",
	error: null,
	isSubmitting: false,
	canFlipToSignup: false,
	copy: {},
};

type Action =
	| { type: "OPEN"; opts: OpenModalOpts; initialStage: Stage }
	| { type: "CLOSE" }
	| { type: "NOOP" }
	| { type: "SET_STAGE"; stage: Stage }
	| { type: "SET_EMAIL"; email: string }
	| { type: "SET_ERROR"; error: string | null }
	| { type: "SET_SUBMITTING"; isSubmitting: boolean }
	| { type: "SET_CAN_FLIP_TO_SIGNUP"; can: boolean }
	| { type: "SET_PREFILL_NAMES"; first: string; last: string };

function reducer(state: AuthState = DEFAULT_STATE, action: Action): AuthState {
	switch (action.type) {
		case "OPEN":
			return {
				...DEFAULT_STATE,
				isOpen: true,
				intent: action.opts.intent,
				stage: action.initialStage,
				email: action.opts.prefillEmail ?? "",
				copy: action.opts.copy ?? {},
			};
		case "CLOSE":
			return { ...state, isOpen: false };
		case "NOOP":
			return state;
		case "SET_STAGE":
			// Reset canFlipToSignup when leaving emailEntry
			return {
				...state,
				stage: action.stage,
				canFlipToSignup:
					action.stage === "emailEntry" ? state.canFlipToSignup : false,
			};
		case "SET_EMAIL":
			return { ...state, email: action.email };
		case "SET_ERROR":
			return { ...state, error: action.error };
		case "SET_SUBMITTING":
			return { ...state, isSubmitting: action.isSubmitting };
		case "SET_CAN_FLIP_TO_SIGNUP":
			return { ...state, canFlipToSignup: action.can };
		case "SET_PREFILL_NAMES":
			return {
				...state,
				prefillFirstName: action.first,
				prefillLastName: action.last,
			};
		default:
			return state;
	}
}

function getEnrollmentStatus(): "guest" | "loggedInNotEnrolled" | "enrolled" {
	if (window.leat?.loggedIn !== "1") return "guest";
	if (window.leatEnrollmentData?.status === "enrolled") return "enrolled";
	return "loggedInNotEnrolled";
}

const actions = {
	openModal(opts: OpenModalOpts): Action {
		const status = getEnrollmentStatus();
		if (status === "enrolled") {
			return { type: "NOOP" };
		}
		return {
			type: "OPEN",
			opts,
			initialStage: initialStageForIntent(
				opts.intent,
				status,
				Boolean(opts.prefillEmail?.trim()),
			),
		};
	},
	closeModal(): Action {
		return { type: "CLOSE" };
	},
	setStage(stage: Stage): Action {
		return { type: "SET_STAGE", stage };
	},
	setEmail(email: string): Action {
		return { type: "SET_EMAIL", email };
	},
	setError(error: string | null): Action {
		return { type: "SET_ERROR", error };
	},
	setSubmitting(isSubmitting: boolean): Action {
		return { type: "SET_SUBMITTING", isSubmitting };
	},
	setCanFlipToSignup(can: boolean): Action {
		return { type: "SET_CAN_FLIP_TO_SIGNUP", can };
	},
	setPrefillNames(first: string, last: string): Action {
		return { type: "SET_PREFILL_NAMES", first, last };
	},
};

const selectors = {
	isOpen: (state: AuthState) => state.isOpen,
	getIntent: (state: AuthState) => state.intent,
	getStage: (state: AuthState) => state.stage,
	getEmail: (state: AuthState) => state.email,
	getError: (state: AuthState) => state.error,
	isSubmitting: (state: AuthState) => state.isSubmitting,
	getCopy: (state: AuthState) => state.copy,
	getPrefillFirstName: (state: AuthState) => state.prefillFirstName,
	getPrefillLastName: (state: AuthState) => state.prefillLastName,
	getCanFlipToSignup: (state: AuthState) => state.canFlipToSignup,
};

const storeConfig = { reducer, actions, selectors };

export const authStore: StoreDescriptor<typeof storeConfig> = createReduxStore(
	AUTH_STORE_NAME,
	storeConfig,
);

register(authStore);

export type AuthStoreActions = typeof actions;
export type AuthStoreSelectors = typeof selectors;
